import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Singleton } from '../models/singleton';
import { Tag } from '../models/tag';
import { CalcQty } from '../models/calc-qty';
import { Product } from '../models/product';
import { Customer } from '../models/customer';
import { ProductGroup } from '../models/product-group';
import { CustomerGroup } from '../models/customer-group';
import { CoreResponse } from '../models/core-response';
import { map, tap } from 'rxjs/operators';
import { SingletonClassEnum } from '../constants/enums';
import { TransactionTagGroup } from '../models/transaction-tag-group';
import { TagGroupItem } from '../models/tag-group-item';
import { TagGroup } from '../models/tag-group';
import { TagGroupContext } from '../models/contexts/tag-group-context';
import { HelperService } from './helper.service';

@Injectable()
export class FieldService {
    private singletonUrl = environment.apiEndpoint + '/singleton/';
    private tagUrl = environment.apiEndpoint + '/tag/';
    private calcQtyUrl = environment.apiEndpoint + '/calcQty/';
    private productUrl = environment.apiEndpoint + '/product/';
    private customerUrl = environment.apiEndpoint + '/customer/';
    private productGroupUrl = environment.apiEndpoint + '/productGroup/';
    private customerGroupUrl = environment.apiEndpoint + '/customerGroup/';
    private tagGroupUrl = environment.apiEndpoint + '/tagGroup/';

    private singletons$: Observable<Singleton[]>;
    private tags$: Observable<Tag[]>;
    private calcQtys$: Observable<CalcQty[]>;
    private products$: Observable<Product[]>;
    private customers$: Observable<Customer[]>;
    private productGroups$: Observable<ProductGroup[]>;
    private customerGroups$: Observable<CustomerGroup[]>;

    tagImportUrl: string = this.tagUrl + 'import';
    tagGroupImportUrl: string = this.tagGroupUrl + 'import';
    CUSTOMER_KEY: string = "CUSTOMER";
    PRODUCT_KEY: string = "PRODUCT";
    readonly SINGLETON_FIELD_CLASSES = [
        SingletonClassEnum.Seller,
        SingletonClassEnum.Qty,
        SingletonClassEnum.Date,
        SingletonClassEnum.Text,
        SingletonClassEnum.Tag,
        SingletonClassEnum.Groupable1,
        SingletonClassEnum.Groupable2
    ];

    constructor(private http: HttpClient, private helperService: HelperService) {
    }

    resetSingletonCache() {
        this.singletons$ = null;
    }

    resetTagCache() {
        this.tags$ = null;
    }

    resetCalcQtyCache() {
        this.calcQtys$ = null;
    }

    private resetProductCache() {
        this.products$ = null;
    }

    private resetCustomerCache() {
        this.customers$ = null;
    }

    private resetProductGroupCache() {
        this.productGroups$ = null;
    }

    private resetCustomerGroupCache() {
        this.customerGroups$ = null;
    }

    resetCaches() {
        this.resetSingletonCache();
        this.resetTagCache();
        this.resetCalcQtyCache();
        this.resetCustomerCache();
        this.resetCustomerGroupCache();
        this.resetProductCache();
        this.resetProductGroupCache();
    }

    private resetGroupCache(key: string) {
        if (key === this.CUSTOMER_KEY) {
            this.resetCustomerCache();
            this.resetCustomerGroupCache();
        } else if (key === this.PRODUCT_KEY) {
            this.resetProductCache();
            this.resetProductGroupCache();
        }
    }

    private resetItemCache(key: string) {
        if (key === this.CUSTOMER_KEY) {
            this.resetCustomerCache();
        } else if (key === this.PRODUCT_KEY) {
            this.resetProductCache();
        }
    }

    getSingletons(): Observable<Singleton[]> {
        return this.helperService.getCached(this.singletons$, this.http.get<Singleton[]>(this.singletonUrl));
    }

    getSingletonsByClassId(singletonClassId: number): Observable<Singleton[]> {
        return this.getSingletons().pipe(map(singletons => singletons.filter(x => x.singletonClassId === singletonClassId)));
    }

    insertSingleton(singleton: Singleton): Observable<Singleton> {
        return this.http.post<Singleton>(this.singletonUrl, singleton).pipe(tap(this.resetSingletonCache));
    }

    updateSingleton(singleton: Singleton): Observable<Singleton> {
        return this.http.put<Singleton>(this.singletonUrl, singleton).pipe(tap(this.resetSingletonCache));
    }

    deleteSingleton(id: number): Observable<CoreResponse<any>> {
        return this.http.delete<CoreResponse<any>>(this.singletonUrl + id).pipe(tap(this.resetSingletonCache));
    }

    getTags(): Observable<Tag[]> {
        return this.helperService.getCached(this.tags$, this.http.get<Tag[]>(this.tagUrl));
    }

    getDeletedTagIds(): Observable<number[]> {
        return this.getTags().pipe(map(tags => tags.filter(x => x.isDeleted).map(x => x.id)));
    }

    insertTag(tag: Tag): Observable<Tag> {
        return this.http.post<Tag>(this.tagUrl, tag).pipe(tap(this.resetTagCache));
    }

    updateTag(tag: Tag): Observable<Tag> {
        return this.http.put<Tag>(this.tagUrl, tag).pipe(tap(this.resetTagCache));
    }

    deleteTag(id: number): Observable<CoreResponse<any>> {
        return this.http.delete<CoreResponse<any>>(this.tagUrl + id).pipe(tap(this.resetTagCache));
    }

    getCalcQtys(): Observable<CalcQty[]> {
        return this.helperService.getCached(this.calcQtys$, this.http.get<CalcQty[]>(this.calcQtyUrl));
    }

    getConstantCalcQtys(): Observable<CalcQty[]> {
        return this.getCalcQtys().pipe(map(calcQtys => calcQtys.filter(x => !x.formula.includes('['))));
    }

    insertCalcQty(calcQty: CalcQty): Observable<CalcQty> {
        return this.http.post<CalcQty>(this.calcQtyUrl, calcQty).pipe(tap(this.resetCalcQtyCache));
    }

    updateCalcQty(calcQty: CalcQty): Observable<CalcQty> {
        return this.http.put<CalcQty>(this.calcQtyUrl, calcQty).pipe(tap(this.resetCalcQtyCache));
    }

    deleteCalcQty(id: number): Observable<CoreResponse<any>> {
        return this.http.delete<CoreResponse<any>>(this.calcQtyUrl + id).pipe(tap(this.resetCalcQtyCache));
    }

    getSingletonDeleteConstraints(id: number): Observable<any> {
        return this.http.get<any>(this.singletonUrl + 'deleteConstraints/' + id);
    }

    getCalcQtyDeleteConstraints(id: number): Observable<any> {
        return this.http.get<any>(this.calcQtyUrl + 'deleteConstraints/' + id);
    }

    getFieldNameConstraints(name: string): Observable<any> {
        return this.http.get<any>(this.singletonUrl + 'nameConstraints/' + encodeURIComponent(name));
    }

    getIsMappedColumnNvarcharMax(columnName: string){
        return this.http.get<CoreResponse<boolean>>(this.singletonUrl + `isFieldNvarcharMax/${columnName}`);
    }

    setColumnDataTypeNvarcharMax(column: Singleton){
        return this.http.post<CoreResponse<string>>(this.singletonUrl + `setFieldNvarcharMax`, column);
    }

    convertFormats(fields: any): any {
        fields.forEach(field => {
            field.format = this.singletonToFormatString(field.format, field.singletonClassId);
        });

        return fields;
    }

    singletonToFormatString(format: string, singletonClassId: number) {
        const numericSeparatorRE = /#,([0#])(\b|$)/g;

        format = format ? format : '';
        if (format) {
            if (!singletonClassId || singletonClassId === 2) {
                format = format.toLowerCase();
                if (format === 'c') {
                    format = '$#,##0.00;($#,##0.00)';
                } else if (format === 'p') {
                    format = 'Percent';
                } else if (format.match(numericSeparatorRE)) {
                    format = format.replace(numericSeparatorRE, '#,##$1');
                }
            } else if (singletonClassId === 3) {
                if (format === 'f') {
                    format = 'longDateLongTime';
                }
            }
        }

        return format;
    }

    singletonToSummaryFormatString(format: any, singletonClassId: number) {
        const numericSeparatorRE = /#,([0#])(\b|$)/g;

        format = format ? format : '';
        if (format) {
            if (!singletonClassId || singletonClassId === 2) {
                format = format.toLowerCase();
                if (format === 'c') {
                    format = { type: 'currency', precision: 2 };// 'currency';
                } else if (format === 'p') {
                    format = 'Percent';
                } else if (format.match(numericSeparatorRE)) {
                    format = format.replace(numericSeparatorRE, '#,##$1');
                }
            } else if (singletonClassId === 3) {
                if (format === 'f') {
                    format = 'longDateLongTime';
                }
            }
        }

        return format;
    }

    convertFormatString(formatString: string): string {
        const numericSeparatorRE = /#,([0#])(\b|$)/g;
        let result = formatString ? formatString : '';
        if (result.toLowerCase() === 'c') {
            result = '$#,##0.00;($#,##0.00)';
        } else if (result.toLowerCase() === 'p') {
            result = 'percent';
        } else if (result === 'f') {
            result = 'longDateLongTime';
        } else if (result.match(numericSeparatorRE)) {
            result = result.replace(numericSeparatorRE, '#,##$1');
        }
        return result;
    }

    getTagMapping(): Observable<any[]> {
        return forkJoin([
            this.getSingletonsByClassId(SingletonClassEnum.Tag).pipe(map(tagField => tagField.map(x => +x.refName.split('_')[2]))), 
            this.getTags()
        ]).pipe(map(([tagNos, tags]) =>  {
            const mapping: any = [];
            tagNos.forEach(key => {
                mapping[key] = tags.filter(x => x.tagNo === key).map(x => ({key: x.id.toString(), value: x.name}))
            })
            return mapping;
        }));
    }

    getAllProducts(): Observable<Product[]> {
        return this.helperService.getCached(this.products$, this.http.get<Product[]>(this.productUrl));
    }

    getAllCustomers(): Observable<Customer[]> {
        return this.helperService.getCached(this.customers$, this.http.get<Customer[]>(this.customerUrl));
    }

    getAllProductGroups(): Observable<ProductGroup[]> {
        return this.helperService.getCached(this.productGroups$, this.http.get<ProductGroup[]>(this.productGroupUrl));
    }

    getAllCustomerGroups(): Observable<CustomerGroup[]> {
        return this.helperService.getCached(this.customerGroups$, this.http.get<CustomerGroup[]>(this.customerGroupUrl));
    }

    getFieldNames(): Observable<string[]> {
        return this.getSingletons().pipe(map(singletons => singletons
            .filter(x => x.singletonClassId in this.SINGLETON_FIELD_CLASSES && x.value)
            .map(x => x.value)
            .sort((a, b) => a.localeCompare(b))));
    }

    splitTagsByField(tags: Tag[]): any {
        const splitTags = {};

        for (const tag of tags) {
            tag.visible = !tag.isDeleted;
            if (splitTags[tag.tagNo] === undefined) {
                splitTags[tag.tagNo] = [tag];
            } else {
                splitTags[tag.tagNo].push(tag);
            }
        }

        return splitTags;
    }

    getTransactionTagGroups(): Observable<TransactionTagGroup[]> {
        return this.getSingletons().pipe(map(singletons => [
            new TransactionTagGroup(this.PRODUCT_KEY, singletons.find(x => x.singletonClassId === SingletonClassEnum.Groupable1).value),
            new TransactionTagGroup(this.CUSTOMER_KEY, singletons.find(x => x.singletonClassId === SingletonClassEnum.Groupable2).value)
        ]));
    }

    getTagGroupItems(key: string): Observable<TagGroupItem[]> {
        if (key === this.CUSTOMER_KEY) {
            return this.getAllCustomers().pipe(map(customers => customers.map(x => new TagGroupItem(x.id, x.name, x.importName, x.customerGroupId, x.isDeleted))));
        } else if (key === this.PRODUCT_KEY) {
            return this.getAllProducts().pipe(map(customers => customers.map(x => new TagGroupItem(x.id, x.name, x.importName, x.productGroupId, x.isDeleted))));
        }
    }

    getTagGroups(key: string): Observable<TagGroup[]> {
        if (key === this.CUSTOMER_KEY) {
            return this.getAllCustomerGroups().pipe(map(customerGroups => customerGroups.map(x => new TagGroup(x.id, x.name, x.isDeleted))));
        } else if (key === this.PRODUCT_KEY) {
            return this.getAllProductGroups().pipe(map(productGroups => productGroups.map(x => new TagGroup(x.id, x.name, x.isDeleted))));
        }
    }

    insertTagGroupItem(context: TagGroupContext): Observable<TagGroupItem> {
        return this.http.post<TagGroupItem>(this.tagGroupUrl + 'insertItem', context).pipe(tap(() => this.resetItemCache(context.transactionTagGroupKey)));
    }

    insertTagGroup(context: TagGroupContext): Observable<TagGroup> {
        return this.http.post<TagGroup>(this.tagGroupUrl + 'insertGroup', context).pipe(tap(() => this.resetGroupCache(context.transactionTagGroupKey)));
    }

    updateTagGroupItem(context: TagGroupContext): Observable<number> {
        return this.http.put<number>(this.tagGroupUrl + 'updateItem', context).pipe(tap(() => this.resetItemCache(context.transactionTagGroupKey)));
    }

    updateTagGroup(context: TagGroupContext): Observable<number> {
        return this.http.put<number>(this.tagGroupUrl + 'updateGroup', context).pipe(tap(() => this.resetGroupCache(context.transactionTagGroupKey)));
    }

    renameTransactionTagGroup(key, value): Observable<number> {
        return this.http.put<number>(this.tagGroupUrl + 'rename/' + key + '/' + value, null).pipe(tap(this.resetSingletonCache));
    }

    deleteTagGroupItem(key: string, id: number): Observable<CoreResponse<number>> {
        return this.http.delete<CoreResponse<number>>(this.tagGroupUrl + 'deleteItem/' + key + '/' + id.toString()).pipe(tap(() => this.resetItemCache(key)));
    }

    deleteTagGroup(key: string, id: number): Observable<CoreResponse<number>> {
        return this.http.delete<CoreResponse<number>>(this.tagGroupUrl + 'deleteGroup/' + key + '/' + id.toString()).pipe(tap(() => this.resetGroupCache(key)));
    }

    getTagGroupItemDeleteConstraints(key: string, id: number): Observable<any> {
        return this.http.get<any>(this.tagGroupUrl + 'deleteItemConstraints/' + key + '/' + id.toString());
    }

    getTagGroupDeleteConstraints(key: string, id: number): Observable<any> {
        return this.http.get<any>(this.tagGroupUrl + 'deleteGroupConstraints/' + key + '/' + id.toString());
    }

    getTagGroupRenameConstraints(name: string): Observable<any> {
        return this.http.get<any>(this.tagGroupUrl + 'renameConstraints/' + encodeURIComponent(name));
    }
}
